import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  changeMemberPassword,
  getCurrentUser,
  getDefaultMemberProfileSchema,
  getMemberProfile,
  getMemberProfileSchema,
  saveMemberProfile
} from '../services/authService';
import './Dashboard.css';

function toMonthInputValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const isoMatch = raw.match(/^(\d{4})-(0[1-9]|1[0-2])$/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}`;
  }

  const slashMatch = raw.match(/^(0[1-9]|1[0-2])\/(\d{4})$/);
  if (slashMatch) {
    return `${slashMatch[2]}-${slashMatch[1]}`;
  }

  const dateMatch = raw.match(/^(\d{4})-(0[1-9]|1[0-2])-\d{2}$/);
  if (dateMatch) {
    return `${dateMatch[1]}-${dateMatch[2]}`;
  }

  return raw;
}

function renderField(field, value, onChange) {
  const commonProps = {
    name: field.name,
    required: field.required,
    placeholder: field.placeholder || ''
  };

  if (field.type === 'textarea') {
    return (
      <textarea
        {...commonProps}
        value={value || ''}
        onChange={(event) => onChange(field.name, event.target.value)}
        rows={4}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <select
        {...commonProps}
        value={value || ''}
        onChange={(event) => onChange(field.name, event.target.value)}
      >
        <option value="">Select {field.label}</option>
        {field.options.map((option) => (
          <option key={`${field.name}-${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'file') {
    return (
      <input
        {...commonProps}
        type="file"
        accept={field.accept || 'image/*'}
        onChange={(event) => onChange(field.name, event.target.files?.[0] || null)}
      />
    );
  }

  if (field.type === 'month') {
    const monthValue = toMonthInputValue(value);

    return (
      <input
        {...commonProps}
        type="month"
        value={monthValue}
        placeholder="YYYY-MM"
        pattern="\d{4}-(0[1-9]|1[0-2])"
        title="Use YYYY-MM"
        onChange={(event) => onChange(field.name, event.target.value)}
      />
    );
  }

  return (
    <input
      {...commonProps}
      type={field.type || 'text'}
      value={value || ''}
      onChange={(event) => onChange(field.name, event.target.value)}
    />
  );
}

export default function MemberDashboard() {
  const { user, setUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [schema, setSchema] = useState(getDefaultMemberProfileSchema());
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [fields, memberProfile] = await Promise.all([
          getMemberProfileSchema(),
          getMemberProfile()
        ]);
        setSchema(fields);
        setProfile(memberProfile);
      } catch (err) {
        setError(err.message || 'Unable to load member dashboard.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const overviewItems = useMemo(
    () =>
      schema
        .filter((field) => field.type !== 'file')
        .map((field) => ({
          key: field.name,
          label: field.label,
          value: profile?.[field.name]
        }))
        .filter((item) => item.value),
    [profile, schema]
  );

  const handleFieldChange = (name, value) => {
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setInfo('');

    try {
      await saveMemberProfile(profile, schema);
      const [freshProfile, freshUser] = await Promise.all([
        getMemberProfile(),
        getCurrentUser().catch(() => null)
      ]);
      setProfile(freshProfile);
      if (freshUser) {
        setUser(freshUser);
      }
      setInfo('Profile saved successfully.');
    } catch (err) {
      setError(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');

    if (passwordForm.password !== passwordForm.password_confirmation) {
      setError('New password and confirm password do not match.');
      return;
    }

    setPasswordSaving(true);
    try {
      const res = await changeMemberPassword(passwordForm);
      setInfo(res.message || 'Password changed successfully.');
      setPasswordForm({
        current_password: '',
        password: '',
        password_confirmation: ''
      });
    } catch (err) {
      setError(err.message || 'Could not change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  return (
    <div className="dashboard-shell">
      <header className="dashboard-shell-header">
        <h1>Member dashboard</h1>
        <div className="dashboard-shell-actions">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((prev) => !prev)}
            aria-expanded={sidebarOpen}
            aria-controls="member-dashboard-sidebar"
          >
            Menu
          </button>
          <Link to="/">Public site</Link>
          <button type="button" onClick={logout}>Logout</button>
        </div>
      </header>

      {error && <p className="error-text">{error}</p>}
      {info && <p className="success-text">{info}</p>}

      <div className="member-layout">
        <aside
          id="member-dashboard-sidebar"
          className={`dashboard-sidebar member-sidebar${sidebarOpen ? ' open' : ''}`}
        >
          <h3>Member Menu</h3>
          <button
            type="button"
            className={activeTab === 'profile' ? 'active' : ''}
            onClick={() => switchTab('profile')}
          >
            My Profile
          </button>
          <button
            type="button"
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => switchTab('overview')}
          >
            Profile Overview
          </button>
          <button
            type="button"
            className={activeTab === 'password' ? 'active' : ''}
            onClick={() => switchTab('password')}
          >
            Change Password
          </button>

          <div className="member-sidebar-summary">
            <p><strong>{profile.name || user?.fullName || 'Member'}</strong></p>
            <p>{profile.designation || 'Profile setup required'}</p>
            <p>{schema.length} profile fields connected</p>
          </div>
        </aside>

        <main className="member-main">
          {activeTab === 'profile' && (
            <section className="panel">
              <h2>My profile</h2>
              <p>Update your information and keep your member profile current.</p>
              {loading ? (
                <p>Loading profile...</p>
              ) : (
                <form className="panel-grid" onSubmit={handleSave}>
                  {schema.map((field) => (
                    <label
                      key={field.name}
                      className={field.type === 'textarea' || field.type === 'file' ? 'field-span-full' : ''}
                    >
                      {field.label}
                      {renderField(field, profile?.[field.name], handleFieldChange)}
                      {field.helperText && <span className="field-help">{field.helperText}</span>}
                    </label>
                  ))}

                  {profile.profile_photo_url && (
                    <div className="profile-photo-preview field-span-full">
                      <img src={profile.profile_photo_url} alt={profile.name || 'Profile'} />
                    </div>
                  )}

                  <button type="submit" className="primary-btn" disabled={saving}>
                    {saving ? 'Saving...' : 'Save profile'}
                  </button>
                </form>
              )}
            </section>
          )}

          {activeTab === 'overview' && (
            <section className="panel">
              <h2>Profile overview</h2>
              <p>Your saved member profile from the backend.</p>
              {loading ? (
                <p>Loading profile...</p>
              ) : (
                <div className="profile-overview-grid">
                  {profile.profile_photo_url && (
                    <div className="profile-photo-card">
                      <img src={profile.profile_photo_url} alt={profile.name || 'Profile'} />
                    </div>
                  )}
                  {overviewItems.map((item) => (
                    <div className="overview-card" key={item.key}>
                      <h3>{item.label}</h3>
                      <p>{item.value}</p>
                    </div>
                  ))}
                  {!overviewItems.length && (
                    <p>No member profile data returned yet.</p>
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === 'password' && (
            <section className="panel">
              <div className="security-panel">
                <div className="security-panel-copy">
                  <p className="security-eyebrow">Security</p>
                  <h2>Change password</h2>
                  <p>Protect your account with a strong password that you do not reuse elsewhere.</p>
                  <div className="security-tips">
                    <div className="security-tip-card">
                      <h3>Use a unique password</h3>
                      <p>Choose something only used for this alumni account.</p>
                    </div>
                    <div className="security-tip-card">
                      <h3>Make it stronger</h3>
                      <p>Use a mix of uppercase, lowercase, numbers, and symbols.</p>
                    </div>
                  </div>
                </div>

                <form className="password-panel security-form-card" onSubmit={handlePasswordChange}>
                  <div className="security-form-header">
                    <p className="security-form-kicker">Password update</p>
                    <h3>Set a new secure password</h3>
                    <p>Enter your current password first, then choose a new one and confirm it.</p>
                  </div>

                  <label className="security-field">
                    <span>Current password</span>
                    <input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(event) =>
                        setPasswordForm((prev) => ({ ...prev, current_password: event.target.value }))
                      }
                      required
                    />
                  </label>
                  <label className="security-field">
                    <span>New password</span>
                    <input
                      type="password"
                      value={passwordForm.password}
                      onChange={(event) =>
                        setPasswordForm((prev) => ({ ...prev, password: event.target.value }))
                      }
                      required
                    />
                  </label>
                  <label className="security-field">
                    <span>Confirm new password</span>
                    <input
                      type="password"
                      value={passwordForm.password_confirmation}
                      onChange={(event) =>
                        setPasswordForm((prev) => ({ ...prev, password_confirmation: event.target.value }))
                      }
                      required
                    />
                  </label>
                  <div className="security-form-actions">
                    <button type="submit" className="primary-btn" disabled={passwordSaving}>
                      {passwordSaving ? 'Updating...' : 'Update password'}
                    </button>
                  </div>
                </form>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
